import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { createApiUrlWithPrefix } from '../config/api';
import Modal, { ModalAlert, ModalFooterBar } from './ui/Modal';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTemporaryPassword('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(createApiUrlWithPrefix('auth/forgot-password'), {
        email
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      setSuccess('Password reset successfully!');
      setTemporaryPassword(response.data.temporaryPassword);
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess('');
    setTemporaryPassword('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Forgot Password"
      subtitle="Enter your email to receive a temporary password"
      icon={KeyRound}
      variant="indigo"
      size="sm"
      bodyClassName="p-0"
      footer={
        <ModalFooterBar hint="You will need to change the password after logging in.">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            disabled={loading}
          >
            Close
          </button>
          <button
            type="submit"
            form="forgot-password-form"
            disabled={loading}
            className="px-5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </ModalFooterBar>
      }
    >
      <form id="forgot-password-form" onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="input-field"
            placeholder={t('auth.enterYourEmail')}
          />
        </div>

        {error && <ModalAlert type="error">{error}</ModalAlert>}
        {success && <ModalAlert type="success">{success}</ModalAlert>}

        {temporaryPassword && (
          <ModalAlert type="info">
            <p className="font-semibold mb-2">Your temporary password:</p>
            <p className="font-mono text-base bg-white p-2 rounded border border-blue-200">
              {temporaryPassword}
            </p>
            <p className="text-sm mt-2 opacity-90">
              Use this password to log in, then change it immediately.
            </p>
          </ModalAlert>
        )}
      </form>
    </Modal>
  );
};

export default ForgotPasswordModal;
